import { observable, makeObservable } from "mobx";
import { fetchObWaveform, getObPatientSummary } from "../../serviceCalls";
import { DateTime, Duration } from "luxon";
import _ from "lodash";

export const minuteFormat = "yyyy-MM-dd HH:mm";

export default class ObSummaryStore {
  WAVEFORM_TIMER_DURATION = 15 * 1000;
  SUMMARY_TIMER_DURATION = 2 * 60 * 1000;

  endTime;
  startTime;
  expandedTile;
  _liveMode = true;
  liveModeLoading = false;
  loading;
  selectedAnnotation;
  tiles = {};
  _waveform;

  maxLengthMs = 10 * 60 * 1000;
  adusername = sessionStorage.username;

  constructor() {
    makeObservable(this, {
      endTime: observable,
      startTime: observable,
      expandedTile: observable,
      _liveMode: observable,
      liveModeLoading: observable,
      loading: observable,
      selectedAnnotation: observable,
      tiles: observable,
      _waveform: observable,
    });

    this.setChartTimeLive();
  }

  dispose() {
    clearTimeout(this.waveformTimeout);
    clearTimeout(this.summaryTimeout);
    clearTimeout(this.animateChartTimer);
    this.refreshSummary = _.noop;
    this.refreshWaveform = _.noop;
    this.startSummaryTimer = _.noop;
    this.startWaveformTimer = _.noop;
    this.animateChart = _.noop;
    this._liveMode = false;
  }
  /**
   * Takes the existing waveform data and concatenates it with the new data.
   * @param {Object} newWaveformData
   * @param {*} newWaveformData.waveform waveform object from API
   * @param {Boolean} newWaveformData.forceUpdate - if true, will force update the waveform
   */
  concatenateWaveformRecords = ({ waveform, forceUpdate }) => {
    //We want to force a refresh instead of concatenating OR
    //If the existing waveform is empty, replace it with the new complete waveform
    if (forceUpdate || !this.waveform?.records) {
      this.waveform = waveform;
    } else {
      // the setter will handle sorting and de-duping.
      this.waveform = {
        records: this.waveform.records.concat(waveform.records).slice(-12 * 60), // maximum of 12 hours of data.
      };
    }
  };

  fetchObSummary = async ({ asmpid }) => {
    this.loading = true;
    this.asmpid = asmpid;
    await Promise.all([
      this.refreshWaveform(),
      this.getObPatientSummarySvc({
        asmpid,
        adusername: this.adusername,
      }),
    ])
      .then((responses) => {
        this.tiles = responses[1].tiles;
      })

      .finally(() => {
        this.loading = false;
        this.startSummaryTimer();
        this.startWaveformTimer();
      });
  };

  markOverlappingAnnotations(summary) {
    summary.tiles.annotations?.model?.annotationGroups.forEach(
      ({ annotations }) => {
        annotations.forEach((annotation) => {
          annotation.dateTime = DateTime.fromISO(annotation.recordTime);
        });
        annotations.forEach((annotation) => {
          const minute = annotation.dateTime.toFormat(minuteFormat);
          const overlappingAnnotations = annotations.filter(
            (annotation) =>
              annotation.dateTime.toFormat(minuteFormat) === minute,
          );
          if (overlappingAnnotations.length > 1) {
            overlappingAnnotations.forEach(
              (overlappingAnnotation) =>
                (overlappingAnnotation.isOverlapping = true),
            );
          }
        });
      },
    );
    return summary;
  }

  getChartLengthInMillis({ startTime, endTime }) {
    if (startTime && endTime) {
      return endTime.diff(startTime);
    } else {
      return null;
    }
  }

  /**
   * Wrapper for calling getObPatientSummary
   * @param {Object} params
   * @param {*} params.adusername
   * @param {*} params.asmpid
   * @param {*} params.fromdate Only fetches waveform data from this datetime forward
   * @returns
   */

  getObPatientSummarySvc({ adusername, asmpid, fromdate }) {
    if (!asmpid) return Promise.reject(new Error("asmpid is required"));
    return getObPatientSummary({
      adusername,
      asmpid,
      fromdate,
    }).then(this.markOverlappingAnnotations);
  }

  get chartLengthMs() {
    return this.endTime - this.startTime;
  }

  /**
   * Given specific params, refreshes the waveform data and updates the state
   * @returns Promise
   */
  refreshWaveform() {
    const fromdate = _.last(this.waveform?.records)?.startTime.toISO();

    this.loadingWaveform = true;
    this.refreshWaveformController = new AbortController();
    return fetchObWaveform(
      { asmpid: this.asmpid, fromdate },
      { controller: this.refreshWaveformController },
    )
      .then((waveform) => {
        if (fromdate) {
          this.concatenateWaveformRecords({ waveform });
        } else {
          this.waveform = waveform || { records: [] };
        }
        // Adjust the time if it drifts more that one minute.
        const lastEndTime = _.last(this.waveform.records)?.endTime;
        if (this.liveMode && lastEndTime?.isValid) {
          const diff = DateTime.local().diff(lastEndTime, "minutes");
          if (diff.minutes > 10) {
            this.endTime = lastEndTime;
            this.startTime = lastEndTime.minus({ minutes: 10 });
            clearTimeout(this.animateChartTimer);
          } else {
            this.animateChart();
          }
        }
      })
      .finally(() => {
        this.loadingWaveform = false;
        if (this.liveMode) this.startWaveformTimer();
      });
  }

  delay = Duration.fromMillis(this.WAVEFORM_TIMER_DURATION);
  setChartTimeLive() {
    this.endTime = DateTime.local().minus(this.delay);
    this.startTime = this.endTime.minus({ minutes: 10 }).minus(this.delay);
  }

  startWaveformTimer() {
    clearTimeout(this.waveformTimeout);
    this.waveformTimeout = setTimeout(
      () => this.refreshWaveform(),
      this.WAVEFORM_TIMER_DURATION,
    );
  }

  animateChart() {
    clearTimeout(this.animateChartTimer);
    if (this.liveMode) {
      this.setChartTimeLive();
      this.animateChartTimer = setTimeout(() => {
        if (!this.liveMode) return;
        this.animateChart();
      }, 1000);
    }
  }

  refreshSummary() {
    this.loadingSummary = true;
    this.getObPatientSummarySvc({
      asmpid: this.asmpid,
      adusername: this.adusername,
    })
      .then((summary) => (this.tiles = summary.tiles))
      .finally(() => {
        this.loadingSummary = false;
        this.startSummaryTimer();
      });
  }

  startSummaryTimer() {
    clearTimeout(this.summaryTimeout);
    this.summaryTimeout = setTimeout(() => {
      this.refreshSummary();
    }, this.SUMMARY_TIMER_DURATION);
  }

  get liveMode() {
    return this._liveMode;
  }

  set liveMode(liveMode) {
    if (this.liveMode === liveMode) return;
    if (liveMode) {
      this._liveMode = true;
      // We should check that liveMode isn't already triggering a load, to avoid request spamming.
      if (!this.liveModeLoading) {
        this.liveModeLoading = true;
        this.refreshWaveform().finally(() => {
          this.liveModeLoading = false;
        });
      }
    } else {
      // We need to abort any calls in progress because when they finish, they'll snap the display back to the right side.
      if (
        this.refreshWaveformController &&
        !this.refreshWaveformController.signal.aborted
      ) {
        this.refreshWaveformController.abort();
      }
      this._liveMode = false;
      clearTimeout(this.waveformTimeout);
    }
  }

  /**
   * Converts all ISO-8601 time-based time strings to DateTime objects for each record.
   * and then decodes the WaveData into a series of points and values
   * @param {*} record
   * @returns
   */
  transformRecord(record) {
    //Convert record times to Luxon DateTime
    if (!(record.startTime instanceof DateTime)) {
      record.startTime = DateTime.fromISO(record.startTime);
      record.endTime = DateTime.fromISO(record.endTime);
      record.waves = record.waves.map(decodeWaveData);
    }
    return record;
  }

  get waveform() {
    return this._waveform;
  }

  set waveform(waveform) {
    const records = waveform.records.map(this.transformRecord);
    const recordMap = new Map();
    records.forEach((record) =>
      recordMap.set(record.startTime.toFormat(minuteFormat), record),
    );
    this._waveform = { records: [...recordMap.values()] };
    this.waveformCacheKey = performance.now();
  }

  /**
   * Calculates a new start time from the current startTime, endTime, and maxTime
   * @param {*} params
   * @param {DateTime} params.startTime Start of viewport
   * @param {DateTime} params.endTime End of viewport
   * @param {DateTime} params.maxTime Total end
   * @returns {DateTime}
   */
  getNewStartTime({ startTime, endTime, maxTime }) {
    if (startTime && endTime && maxTime) {
      return startTime
        .minus({ milliseconds: endTime?.toMillis() })
        .plus({ milliseconds: maxTime?.toMillis() });
    } else {
      return null;
    }
  }

  /**
   * Wrapper for some business logic that returns the breadth of the chart
   * @param {Object} params
   * @param {DateTime} params.maxTime
   * @param {DateTime} params.startTime
   * @param {DateTime} params.endTime
   */
  getLiveModeTimeParameters({ maxTime, startTime, endTime }) {
    if (!maxTime) {
      maxTime = this.getMaxTime(this.waveform?.records);
    }
    const tenMinutesAgo = maxTime?.minus({ minutes: 10 });
    const newStartTime = this.getNewStartTime({ startTime, endTime, maxTime });
    return { maxTime, tenMinutesAgo, newStartTime };
  }

  get minTime() {
    return this.getMinTime(this.waveform?.records);
  }

  getMinTime = (records) => {
    if (records?.length) {
      return records[0].startTime;
    } else {
      return null;
    }
  };

  get maxTime() {
    return this.getMaxTime(this.waveform?.records);
  }

  /**
   * Gets the last start time from existing records.
   * We use this to "pick up where we left off" to not refetch existing data
   * @param {*} records
   * @returns
   */
  getMaxStartTime = (records) => {
    return _.last(records)?.startTime || null;
  };

  /**
   * Gets the last endTime from records.
   * @param {*} records
   * @returns {DateTime} Last endTime from records,
   * unless that is greater than "now", then returns "now" which is one second ago.
   */
  getMaxTime = (records) => {
    const oneSecondAgo = DateTime.now().minus({ seconds: 1 });
    const lastEndTime = _.last(records)?.endTime;
    if (!records || records?.length === 0 || lastEndTime > oneSecondAgo) {
      return oneSecondAgo;
    }
    return lastEndTime;
  };

  /**
   * Shifts chart to position of Annotation
   * @param {DateTime} recordTime Time of record
   */
  shiftChartToAnnotation({ dateTime }) {
    const chartLengthMs = this.chartLengthMs;
    this.endTime = DateTime.min(
      dateTime.plus(chartLengthMs / 2),
      DateTime.now(),
    );
    this.startTime = this.endTime.minus(chartLengthMs);
  }

  selectAnnotation(annotation, fromChart) {
    const wasLiveMode = this.liveMode;
    this.liveMode = false;
    if (
      fromChart &&
      this.selectedAnnotation?.dateTime.toFormat(minuteFormat) ===
        annotation.dateTime.toFormat(minuteFormat)
    ) {
      this.selectedAnnotation = null;
    } else {
      this.selectedAnnotation =
        this.selectedAnnotation === annotation ? null : annotation;
    }
    if (!fromChart) {
      const { dateTime } = annotation;
      if (wasLiveMode) {
        this.shiftChartToAnnotation({ dateTime });
      } else {
        this.refreshWaveform().then(() =>
          this.shiftChartToAnnotation({ dateTime }),
        );
      }
    }
  }
}
/**
 * Simply sorts on a property in an array of objects.
 * Source: https://stackoverflow.com/questions/64801992/how-to-perform-sort-on-array-of-luxon-objects
 * @param {string} propertyName
 * @returns
 */
export const createPropertySorter = (propertyName) => (a, b) =>
  a[propertyName] < b[propertyName]
    ? -1
    : a[propertyName] > b[propertyName]
      ? 1
      : 0;

/**
 * Decodes a string of base64 data from wave data to a Uint8Array of points and values.
 * @param wave
 * @returns {*}
 */
export const decodeWaveData = (wave) => {
  if (!wave.data) return wave;
  const values = Uint8Array.from(atob(wave.data), (c) => c.charCodeAt(0));
  wave.points = _.map(values, (value) => {
    return {
      value,
    };
  });
  wave.values = values;
  wave.data = undefined;
  return wave;
};
